import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualAlert from '../../components/manual/ManualAlert';

function ManualIntroducaoPage() {
  return (
    <div className="manual-page">
      <ManualBreadcrumb items={[{ label: 'Introdução ao Sistema' }]} />
      
      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-info-circle me-3"></i>
          Introdução ao Sistema
        </h1>
        <p className="manual-subtitle">
          Conheça o Sistema de Movimentação e como ele pode ajudar no seu trabalho
        </p>
      </div>

      <ManualSection icon="bi-bullseye" title="O que é o Sistema de Movimentação?">
        <p>
          O Sistema de Movimentação é uma ferramenta desenvolvida pela SEJUS-DF para auxiliar no
          acompanhamento de adolescentes no sistema socioeducativo. Ele permite registrar e
          acompanhar toda a trajetória do adolescente desde sua entrada até sua saída.
        </p>
        <p>
          Com ele, você pode:
        </p>
        <ul className="manual-list">
          <li>Cadastrar informações de adolescentes</li>
          <li>Registrar movimentações (entradas e saídas)</li>
          <li>Documentar atendimentos realizados</li>
          <li>Acompanhar a situação atual de cada adolescente</li>
          <li>Gerar relatórios e estatísticas</li>
          <li>Visualizar informações em tempo real</li>
        </ul>
      </ManualSection>

      <ManualSection icon="bi-diagram-3" title="Como o Sistema Funciona?">
        <p>
          O sistema organiza as informações em módulos, cada um com uma função específica:
        </p>
        
        <div className="manual-feature-grid">
          <div className="manual-feature">
            <i className="bi bi-people"></i>
            <h4>Adolescentes</h4>
            <p>Cadastro central com todos os dados pessoais e histórico</p>
          </div>
          
          <div className="manual-feature">
            <i className="bi bi-arrow-left-right"></i>
            <h4>Movimentações</h4>
            <p>Registro de cada entrada e saída do sistema</p>
          </div>
          
          <div className="manual-feature">
            <i className="bi bi-clipboard-check"></i>
            <h4>Atendimentos</h4>
            <p>Documentação de atendimentos por promotoria, defensoria, saúde, etc</p>
          </div>
          
          <div className="manual-feature">
            <i className="bi bi-speedometer2"></i>
            <h4>Painel de Controle</h4>
            <p>Visão geral com números e informações em tempo real</p>
          </div>
          
          <div className="manual-feature">
            <i className="bi bi-pie-chart"></i>
            <h4>Relatórios</h4>
            <p>Análises e dados para tomada de decisão</p>
          </div>
          
          <div className="manual-feature">
            <i className="bi bi-gear"></i>
            <h4>Configurações</h4>
            <p>Gestão de usuários e tabelas de apoio do sistema</p>
          </div>
        </div>
      </ManualSection>

      <ManualSection icon="bi-shield-lock" title="Sistema de Permissões (RBAC)">
        <p>
          O sistema utiliza um controle de acesso baseado em perfis e permissões (RBAC - Role-Based Access Control).
          As permissões são cadastradas no servidor e podem evoluir com o sistema. Na prática, elas controlam
          a exibição do menu, o acesso às páginas e as operações da API.
        </p>
        
        <div className="manual-help-box mb-4">
          <h6 className="mb-3">Principais grupos de permissões:</h6>
          <div className="row">
            <div className="col-md-6">
              <ul>
                <li><strong>adolescentes.*</strong> - Visualizar, criar, editar e excluir adolescentes conforme regra de negócio</li>
                <li><strong>movimentacoes.*</strong> - Visualizar, criar, editar, finalizar, reabrir e excluir movimentações</li>
                <li><strong>atendimentos.*</strong> - Visualizar, criar, editar e excluir atendimentos</li>
                <li><strong>usuarios.*</strong> - Gerenciar usuários do sistema</li>
                <li><strong>relatorios.view</strong> - Acessar indicadores e gráficos operacionais</li>
              </ul>
            </div>
            <div className="col-md-6">
              <ul>
                <li><strong>config.*</strong> - Gerenciar tabelas de apoio, perfis e permissões</li>
                <li><strong>config.promotores</strong> - Gerenciar promotores usados nos atendimentos</li>
                <li><strong>config.motivos_mba</strong> - Gerenciar motivos usados no fluxo de MBA</li>
                <li><strong>admin.logs</strong> - Acessar logs e auditoria</li>
                <li><strong>admin.full_access</strong> - Permissão administrativa ampla</li>
              </ul>
            </div>
          </div>
        </div>

        <ManualAlert type="info">
          <strong>Formato das Permissões:</strong> As permissões seguem o padrão <code>módulo.ação</code>,
          por exemplo: <code>adolescentes.view</code>, <code>movimentacoes.create</code>, <code>config.perfis</code>.
          O sistema também usa permissões específicas como <code>movimentacoes.reopen</code>,
          <code>adolescentes.delete_with_movimentacoes</code> e <code>admin.logs</code> para ações sensíveis.
        </ManualAlert>

        <ManualAlert type="success" title="Atualização em Tempo Real">
          O sistema utiliza <strong>WebSocket (Reverb)</strong> para atualizar suas permissões automaticamente.
          Se um administrador alterar suas permissões enquanto você está usando o sistema, as mudanças
          serão aplicadas imediatamente sem precisar fazer login novamente.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-person-badge" title="Perfis de Usuário">
        <p>
          O sistema possui <strong>7 perfis principais</strong>, cada um com permissões específicas:
        </p>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-shield-fill-exclamation"></i>
            <h4>Administrador SEJUS</h4>
          </div>
          <p>
            Acesso total ao sistema. Pode gerenciar usuários, perfis, permissões e todas as configurações.
            Tem acesso a logs de auditoria e pode realizar todas as operações de CRUD (criar, ler, atualizar, deletar).
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-person-check"></i>
            <h4>Operador</h4>
          </div>
          <p>
            Operações principais do dia a dia: cadastrar e editar adolescentes, registrar movimentações,
            criar atendimentos e acessar o painel de controle. Perfil ideal para uso operacional diário.
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-person-check"></i>
            <h4>Técnico SEJUS</h4>
          </div>
          <p>
            Pode visualizar adolescentes e movimentações, registrar e editar atendimentos técnicos.
            Acesso aos relatórios do sistema para acompanhamento de casos.
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-bank"></i>
            <h4>Representante MPDFT (Promotor)</h4>
          </div>
          <p>
            Acesso de consulta para visualizar adolescentes, movimentações e atendimentos. Pode gerar
            e exportar relatórios específicos do Ministério Público.
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-scale"></i>
            <h4>Representante DPDF (Defensor)</h4>
          </div>
          <p>
            Acesso de consulta para visualizar adolescentes, movimentações e atendimentos. Pode gerar
            e exportar relatórios específicos da Defensoria Pública.
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-clipboard-pulse"></i>
            <h4>Especialista Socioeducativo</h4>
          </div>
          <p>
            Pode visualizar e editar informações de adolescentes, criar movimentações e registrar
            atendimentos especializados (psicologia, serviço social, pedagogia, saúde).
          </p>
        </div>
        
        <div className="manual-profile-card">
          <div className="manual-profile-header">
            <i className="bi bi-eye-fill"></i>
            <h4>Consulta NAI</h4>
          </div>
          <p>
            Perfil somente leitura para o Núcleo de Atendimento Integrado. Visualiza adolescentes,
            movimentações e relatórios básicos sem poder editar informações.
          </p>
        </div>
        
        <ManualAlert type="tip" title="Menu Dinâmico">
          O menu lateral e os itens disponíveis <strong>mudam automaticamente</strong> de acordo com
          seu perfil. O menu <strong>Tabelas de Apoio</strong>, por exemplo, mostra apenas as tabelas
          que você tem permissão para gerenciar. O manual fica disponível para usuários autenticados.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-compass" title="Navegando pelo Sistema">
        <p>
          O sistema possui uma interface intuitiva e fácil de usar:
        </p>
        
        <div className="manual-navigation-guide">
          <div className="manual-nav-item">
            <i className="bi bi-list"></i>
            <div>
              <h5>Menu Lateral</h5>
              <p>À esquerda da tela, acesse os principais módulos do sistema</p>
            </div>
          </div>
          
          <div className="manual-nav-item">
            <i className="bi bi-three-dots-vertical"></i>
            <div>
              <h5>Barra Superior</h5>
              <p>No topo, acesse tabelas de apoio e seu perfil de usuário</p>
            </div>
          </div>
          
          <div className="manual-nav-item">
            <i className="bi bi-search"></i>
            <div>
              <h5>Campo de Busca</h5>
              <p>Encontre rapidamente adolescentes digitando o nome</p>
            </div>
          </div>
          
          <div className="manual-nav-item">
            <i className="bi bi-plus-circle"></i>
            <div>
              <h5>Botões de Ação</h5>
              <p>Use os botões coloridos para criar novos registros</p>
            </div>
          </div>
        </div>
      </ManualSection>

      <ManualAlert type="success" title="Próximos Passos">
        Agora que você conhece o sistema, explore os outros tópicos do manual para aprender como usar cada funcionalidade.
        Sugerimos começar pelo <strong>"Primeiro Acesso"</strong> para aprender a fazer login e navegar.
      </ManualAlert>
    </div>
  );
}

export default ManualIntroducaoPage;
